import { navigate, onRouteChange } from '../router/router';
import { buildChatContext, sendChatMessageStream, type ChatLanguage, type ChatStreamResult } from '../services/chat-ui';
import { trackChatMessageReceived, trackChatMessageSent, trackLanguageSwitch, trackToolUsage } from '../analytics/plausible';
import { getLanguagePreference, setLanguagePreference } from '../router/guards';

let mounted = false;
let widgetLanguage: ChatLanguage = 'en';
let widgetMessages: Array<{ role: 'user' | 'assistant'; content: string }> = [];

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderLine(role: 'user' | 'assistant', content: string): string {
  const roleClass = role === 'user' ? 'chat-widget__line--user' : 'chat-widget__line--assistant';
  return `<div class="chat-widget__line ${roleClass}">${escapeHtml(content)}</div>`;
}

function parseLanguage(raw: string): ChatLanguage {
  if (raw === 'hi' || raw === 'ta' || raw === 'te') return raw;
  return 'en';
}

function addWidgetMessage(role: 'user' | 'assistant', content: string): void {
  widgetMessages.push({ role, content });
  if (widgetMessages.length > 40) {
    widgetMessages = widgetMessages.slice(-40);
  }
}

function renderCompactCards(result: ChatStreamResult): string {
  const cards = Array.isArray(result.cards) ? result.cards : [];
  if (cards.length === 0) return '';

  const text = cards.slice(0, 2).map((card) => {
    const type = typeof card.type === 'string' ? card.type : 'insight';
    const label = typeof card.title === 'string'
      ? card.title
      : (typeof card.symbol === 'string' ? card.symbol : type);
    return `${label}`;
  }).join(' | ');

  return text ? `Cards: ${text}` : '';
}

function setVisibleByRoute(path: string): void {
  const root = document.getElementById('chat-widget-root');
  if (!root) return;
  root.style.display = path === '/chat' ? 'none' : 'block';
}

export function initChatWidget(): void {
  if (mounted) return;
  mounted = true;

  const root = document.createElement('div');
  root.id = 'chat-widget-root';
  widgetLanguage = parseLanguage(getLanguagePreference());
  root.innerHTML = `
    <button id="chat-widget-fab" class="chat-widget__fab" type="button" aria-label="Open chat widget">CHAT</button>
    <section id="chat-widget-panel" class="chat-widget__panel" aria-label="Chat widget" style="display:none;">
      <header class="chat-widget__head">
        <strong>IndiEye Chat</strong>
        <div>
          <select id="chat-widget-language" aria-label="Widget language">
            <option value="en">EN</option>
            <option value="hi">HI</option>
            <option value="ta">TA</option>
            <option value="te">TE</option>
          </select>
          <button id="chat-widget-open-page" type="button">Open Page</button>
          <button id="chat-widget-close" type="button">Close</button>
        </div>
      </header>
      <div id="chat-widget-stream" class="chat-widget__stream">
        ${renderLine('assistant', 'Ask me for market overview, top headlines, or why a move matters.')}
      </div>
      <form id="chat-widget-form" class="chat-widget__composer">
        <input id="chat-widget-input" type="text" placeholder="Ask quick question..." />
        <button type="submit">Send</button>
      </form>
    </section>
  `;

  document.body.appendChild(root);

  const fab = document.getElementById('chat-widget-fab') as HTMLButtonElement | null;
  const panel = document.getElementById('chat-widget-panel') as HTMLElement | null;
  const closeBtn = document.getElementById('chat-widget-close') as HTMLButtonElement | null;
  const openPageBtn = document.getElementById('chat-widget-open-page') as HTMLButtonElement | null;
  const form = document.getElementById('chat-widget-form') as HTMLFormElement | null;
  const input = document.getElementById('chat-widget-input') as HTMLInputElement | null;
  const languageSelect = document.getElementById('chat-widget-language') as HTMLSelectElement | null;
  const stream = document.getElementById('chat-widget-stream') as HTMLElement | null;

  if (!fab || !panel || !closeBtn || !openPageBtn || !form || !input || !stream || !languageSelect) return;
  languageSelect.value = widgetLanguage;

  const appendLine = (role: 'user' | 'assistant', content: string) => {
    stream.insertAdjacentHTML('beforeend', renderLine(role, content));
    stream.scrollTop = stream.scrollHeight;
  };

  fab.addEventListener('click', () => {
    panel.style.display = panel.style.display === 'none' ? 'grid' : 'none';
    if (panel.style.display !== 'none') input.focus();
    trackToolUsage('chat_widget_toggle');
  });

  closeBtn.addEventListener('click', () => {
    panel.style.display = 'none';
  });

  openPageBtn.addEventListener('click', () => {
    panel.style.display = 'none';
    navigate('/chat');
  });

  languageSelect.addEventListener('change', () => {
    const next = parseLanguage(languageSelect.value);
    if (next === widgetLanguage) return;
    trackLanguageSwitch(widgetLanguage, next);
    widgetLanguage = next;
    setLanguagePreference(next);
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const text = input.value.trim();
    if (!text) return;

    input.value = '';
    appendLine('user', text);
    addWidgetMessage('user', text);
    trackChatMessageSent('widget', widgetLanguage);
    appendLine('assistant', 'Thinking...');

    const placeholder = stream.lastElementChild as HTMLElement | null;

    let live = '';
    const history = buildChatContext(widgetMessages.map((msg, idx) => ({
      id: `widget-${idx}`,
      role: msg.role,
      content: msg.content,
      ts: Date.now(),
    })));

    void sendChatMessageStream({
      message: text,
      language: widgetLanguage,
      messages: history,
      onToken: (token) => {
        live += token;
        if (placeholder) placeholder.textContent = live;
      },
    }).then(response => {
      const message = response.message || live;
      addWidgetMessage('assistant', message);
      const cardLine = renderCompactCards(response);
      if (placeholder) placeholder.textContent = cardLine ? `${message}\n\n${cardLine}` : message;
      else appendLine('assistant', cardLine ? `${message}\n\n${cardLine}` : message);
      trackChatMessageReceived('widget', response.intent || 'general');
    }).catch(err => {
      const fallback = `Chat unavailable: ${(err as Error).message}`;
      addWidgetMessage('assistant', fallback);
      if (placeholder) placeholder.textContent = fallback;
      else appendLine('assistant', fallback);
    });
  });

  onRouteChange((path) => {
    setVisibleByRoute(path);
    if (path === '/chat') panel.style.display = 'none';
  });

  setVisibleByRoute(window.location.hash.slice(1) || '/');
}

export const CHAT_WIDGET_STYLES = `
  #chat-widget-root {
    position: fixed;
    right: 16px;
    bottom: 16px;
    z-index: 210;
  }

  .chat-widget__fab {
    border: 1px solid var(--terminal-accent, #f97316);
    background: color-mix(in srgb, var(--terminal-accent, #f97316) 20%, rgba(0,0,0,0.6));
    color: var(--terminal-text, #f9fafb);
    min-width: 78px;
    height: 40px;
    border-radius: 999px;
    font: 600 12px/1 'IBM Plex Mono', monospace;
    letter-spacing: 0.08em;
    cursor: pointer;
  }

  .chat-widget__panel {
    width: min(380px, calc(100vw - 32px));
    height: min(520px, calc(100vh - 100px));
    margin-top: 10px;
    border: 1px solid var(--terminal-line, rgba(255,255,255,0.16));
    border-radius: 10px;
    background: color-mix(in srgb, var(--terminal-bg-panel, #0f172a) 94%, transparent);
    display: grid;
    grid-template-rows: auto 1fr auto;
    overflow: hidden;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35);
  }

  .chat-widget__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px;
    border-bottom: 1px solid var(--terminal-line, rgba(255,255,255,0.14));
    font-size: 12px;
  }

  .chat-widget__head button,
  .chat-widget__head select,
  .chat-widget__composer button {
    border: 1px solid var(--terminal-line, rgba(255,255,255,0.2));
    background: var(--terminal-bg-elevated, rgba(255,255,255,0.05));
    color: var(--terminal-text, #f9fafb);
    border-radius: 6px;
    padding: 5px 8px;
    font: inherit;
    cursor: pointer;
  }

  .chat-widget__head select {
    font-size: 11px;
  }

  .chat-widget__stream {
    padding: 10px;
    overflow: auto;
    display: grid;
    gap: 8px;
    align-content: start;
  }

  .chat-widget__line {
    border: 1px solid var(--terminal-line, rgba(255,255,255,0.14));
    background: rgba(255,255,255,0.03);
    border-radius: 8px;
    padding: 8px;
    font-size: 12px;
    line-height: 1.45;
    white-space: pre-wrap;
  }

  .chat-widget__line--user {
    border-color: color-mix(in srgb, var(--terminal-accent, #f97316) 45%, transparent);
  }

  .chat-widget__composer {
    border-top: 1px solid var(--terminal-line, rgba(255,255,255,0.14));
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
    padding: 10px;
  }

  .chat-widget__composer input {
    border: 1px solid var(--terminal-line, rgba(255,255,255,0.2));
    background: var(--terminal-bg-elevated, rgba(255,255,255,0.05));
    color: var(--terminal-text, #f9fafb);
    border-radius: 8px;
    padding: 8px;
    font: inherit;
  }

  @media (max-width: 680px) {
    #chat-widget-root {
      right: 10px;
      bottom: 10px;
    }

    .chat-widget__fab {
      min-width: 68px;
      height: 36px;
      font-size: 11px;
    }
  }
`;
